import { cbSemanticCssVars } from '@creditornot/cb-ingredients';
import { DndContext, DragOverlay, pointerWithin } from '@dnd-kit/core';
import React, { useRef } from 'react';
import { VariableSizeGrid } from 'react-window';
import styled from 'styled-components';

import { PhotoShootImageData, PhotoShootItemType, PhotoShootStatus } from 'apis/mips/types';
import { usePluralization } from 'common/hooks';

import { useAutoLinkImages } from './useAutoLinkImages';
import { useImagesManagerDnd } from './useImagesManagerDnd';
import { Breakpoints } from '../../constants/breakpoints';
import { PhotoShootModel } from '../../types';
import { ImageTile } from '../ImageTile/ImageTile';
import { ImagesGallery } from '../ImagesGallery/ImagesGallery';
import { ImagesUploader } from '../ImagesUploader/ImagesUploader';
import { LinksPanel } from '../LinksPanel/LinksPanel';
import { PhotoShootActions } from '../PhotoShootActions';

export type ImagesManagerProps = {
  token: string | null;
  venueId: string;
  venueName?: string;
  images: PhotoShootImageData[];
  items: PhotoShootModel['items'];
  status?: PhotoShootStatus;
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  color: ${cbSemanticCssVars.textPrimary};
`;

const TwoColumns = styled.div`
  display: grid;
  grid-template-columns: 65% 35%;
  flex: 1;
  overflow: auto;

  @media (max-width: ${Breakpoints.MOBILE}px) {
    grid-template-columns: 1fr;
  }
`;

const LeftPanel = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
  gap: 24px;
  border-inline-end: 1px solid ${cbSemanticCssVars.borderPrimary};
  overflow: hidden;
  padding: 32px 0 0;

  @media (max-width: ${Breakpoints.MOBILE}px) {
    padding: 12px 0 0;
    gap: 12px;
  }
`;

const RightPanel = styled.div`
  overflow: hidden;

  @media (max-width: ${Breakpoints.MOBILE}px) {
    border-block-start: 1px solid ${cbSemanticCssVars.borderPrimary};
    min-width: unset;
    max-width: unset;
    flex: 1;
  }
`;

const Footer = styled.div`
  padding: 16px 32px;
  border-block-start: 1px solid ${cbSemanticCssVars.borderPrimary};
  display: flex;
  gap: 8px;
  align-items: center;
  justify-content: space-between;

  @media (max-width: ${Breakpoints.MOBILE}px) {
    font-size: 14px;
    padding: 8px;
    flex-direction: column;
    align-items: stretch;
    text-align: center;
  }
`;

const ButtonsContainer = styled.div`
  display: flex;
  gap: 8px;
`;

const PhotoUploaderContainer = styled.div`
  padding: 0 32px;
  @media (max-width: ${Breakpoints.MOBILE}px) {
    padding: 0 8px;
  }
`;

const PhotosGalleryContainer = styled.div`
  flex: 1;
  min-height: 0;
`;

const ImagesManager = ({ venueId, token, images, items, status }: ImagesManagerProps) => {
  const imagesGalleryRef = useRef<VariableSizeGrid>(null);
  const { FormattedPluralMessage } = usePluralization();
  const { handleDragStart, handleDragEnd, dragState, sensors, dropAnimation } = useImagesManagerDnd({ venueId, token });
  const isReadOnlyMode = status === PhotoShootStatus.APPROVED;
  const handleAutoLinkImages = useAutoLinkImages({ venueId, token, items });

  const allItemsArray = [
    ...items[PhotoShootItemType.HEADER],
    ...items[PhotoShootItemType.MENU_ITEM],
    ...items[PhotoShootItemType.LIST],
  ];

  const handleUploadFinished = (images: PhotoShootModel['images']) => {
    imagesGalleryRef.current?.scrollToItem({
      rowIndex: imagesGalleryRef.current?.props.rowCount,
      align: 'end',
    });

    handleAutoLinkImages(images);
  };

  return (
    <Container>
      <DndContext
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        sensors={sensors}
        collisionDetection={pointerWithin}
      >
        <TwoColumns>
          <LeftPanel>
            {!isReadOnlyMode && (
              <PhotoUploaderContainer>
                <ImagesUploader onUploadFinished={handleUploadFinished} />
              </PhotoUploaderContainer>
            )}

            <PhotosGalleryContainer>
              <ImagesGallery
                ref={imagesGalleryRef}
                images={images}
                photoShootItems={allItemsArray}
                isReadOnly={isReadOnlyMode}
              />
            </PhotosGalleryContainer>
          </LeftPanel>
          <RightPanel>
            <LinksPanel
              menuItems={items[PhotoShootItemType.MENU_ITEM]}
              headerItems={items[PhotoShootItemType.HEADER]}
              listItems={items[PhotoShootItemType.LIST]}
              isReadOnly={isReadOnlyMode}
            />
          </RightPanel>
        </TwoColumns>
        <Footer>
          <FormattedPluralMessage id="photo-shoot.images-count" count={images.length} />

          {status && (
            <ButtonsContainer>
              <PhotoShootActions images={images} allItems={allItemsArray} status={status} />
            </ButtonsContainer>
          )}
        </Footer>

        <DragOverlay dropAnimation={dropAnimation}>
          {dragState.activeImage && <ImageTile image={{ ...dragState.activeImage, name: '' }} isOverlay />}
        </DragOverlay>
      </DndContext>
    </Container>
  );
};

export default ImagesManager;
